import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useSession } from '@supabase/auth-helpers-react';
import { useNavigate } from 'react-router-dom'; // ✨ Import useNavigate at top
import { getMyTutorProfiles, getPublicSetting } from '../api/supabaseAPI';
import Loader from '../components/Loader';
import '../styles/AdminDashboard.css'; // ✨ Reuse AdminDashboard styles for glassmorphism cards

const R2_PUBLIC_BASE_URL_ENV = (import.meta.env.VITE_R2_PUBLIC_BASE_URL || '').replace(/\/$/, '');

export default function TutorModeSelection() {
  const { t } = useTranslation();
  const session = useSession();
  const userId = session?.user?.id;
  const navigate = useNavigate(); // ✨ Hook

  const { data: r2PublicBaseUrl } = useQuery({
    queryKey: ['public_settings', 'r2_public_base_url'],
    queryFn: () => getPublicSetting('r2_public_base_url'),
    staleTime: 1000 * 60 * 30,
    refetchOnMount: false,
  });

  const publicBaseUrl = useMemo(() => {
    const rawBaseUrl = R2_PUBLIC_BASE_URL_ENV || r2PublicBaseUrl || '';
    return rawBaseUrl ? rawBaseUrl.replace(/\/$/, '') : '';
  }, [r2PublicBaseUrl]);

  const { data: myProfiles, isLoading } = useQuery({
    queryKey: ['myTutorProfiles', userId],
    queryFn: () => getMyTutorProfiles(userId),
    enabled: !!userId,
  });

  const getImageUrl = (path) => {
    if (!path) return '/Resources/imagine/default_avatar/default_avatar.jpeg';
    if (path.startsWith('http')) return path;
    return `${publicBaseUrl}/${path}`;
  };

  if (isLoading) return <main className="admin-container"><Loader /></main>;

  return (
    <main className="admin-container">
      <header className="admin-header">
        <h1 className="admin-title">Select Tutor Profile</h1>
        <p style={{ color: 'var(--text-medium)', maxWidth: '600px' }}>
          Choose a language profile to manage your students and sessions.
        </p>
      </header>

      <div className="admin-list-grid" style={{ justifyContent: 'center' }}>
        {myProfiles?.map((profile) => (
          <div 
            key={profile.language} 
            className="application-card"
            style={{ cursor: 'default' }} // Override default cursor if reused
          >
            <div className="app-card-header">
              <div className="app-applicant">
                <div style={{ position: 'relative' }}>
                  <img 
                    src={getImageUrl(profile.avatar)} 
                    alt="Avatar" 
                    className="app-avatar" 
                    style={{objectFit: 'cover'}}
                  />
                </div>
                <div className="app-info">
                  <h3>{profile.full_name}</h3>
                  <p>{profile.headline || 'Tutor'}</p>
                </div>
              </div>
              <span className={`lang-badge ${profile.status === 'paused' ? 'status-paused' : ''}`}>
                {profile.language?.replace('_', '-').toUpperCase()}
              </span>
            </div>
            
            <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className={`tutor-status tutor-status-${profile.status}`}>
                ● {profile.status}
              </span>
              <button 
                className="view-btn"
                style={{ 
                  width: 'auto', 
                  padding: '6px 12px', 
                  fontSize: '0.8rem',
                  opacity: profile.status === 'active' ? 1 : 0.5,
                  cursor: profile.status === 'active' ? 'pointer' : 'not-allowed'
                }}
                disabled={profile.status !== 'active'}
                onClick={() => navigate(`/tutor/workspace/${profile.language}`)} // ✨ Navigate to workspace
              >
                Enter
              </button>
            </div>
          </div>
        ))}

        {(!myProfiles || myProfiles.length === 0) && (
          <div className="empty-state">
            <p>You don't have any tutor profiles yet.</p>
          </div>
        )}
      </div>
    </main>
  );
}
